/**
 * A tiny state-machine primitive (core-data-model §3 / Foundation Rule 6). Statuses are
 * explicit machines with server-enforced legal transitions; illegal transitions are
 * rejected, never silently applied. The guard here is PURE (no I/O) so it can be tested
 * exhaustively and reused by the service layer, which wraps each legal transition in a
 * transaction that also writes an `events` row.
 */
export interface Transition<S extends string> {
  from: S;
  to: S;
  /** The named mutation that drives it (e.g. 'visit.start'). */
  via: string;
  /** True when the transition is fired by the system (a cascade), not a user command. */
  system?: boolean;
}

export interface StateMachine<S extends string> {
  states: readonly S[];
  initial: S;
  terminal: readonly S[];
  transitions: readonly Transition<S>[];
}

export class IllegalTransitionError extends Error {
  constructor(
    public readonly machine: string,
    public readonly from: string,
    public readonly to: string,
    public readonly via?: string,
  ) {
    super(`Illegal ${machine} transition ${from} → ${to}${via ? ` via ${via}` : ''}`);
    this.name = 'IllegalTransitionError';
  }
}

export function canTransition<S extends string>(
  m: StateMachine<S>,
  from: S,
  to: S,
  via?: string,
): boolean {
  return m.transitions.some(
    (t) => t.from === from && t.to === to && (via === undefined || t.via === via),
  );
}

/** The legal target states reachable from `from` (optionally for a specific mutation). */
export function legalTargets<S extends string>(
  m: StateMachine<S>,
  from: S,
  via?: string,
): S[] {
  return m.transitions
    .filter((t) => t.from === from && (via === undefined || t.via === via))
    .map((t) => t.to);
}

/** Resolve the target state for a named mutation from the current state, or null. */
export function targetFor<S extends string>(
  m: StateMachine<S>,
  from: S,
  via: string,
): S | null {
  return m.transitions.find((t) => t.from === from && t.via === via)?.to ?? null;
}

export function isTerminal<S extends string>(m: StateMachine<S>, state: S): boolean {
  return m.terminal.includes(state);
}

/** Throws IllegalTransitionError unless the transition is legal. */
export function assertTransition<S extends string>(
  machineName: string,
  m: StateMachine<S>,
  from: S,
  to: S,
  via?: string,
): void {
  if (!canTransition(m, from, to, via)) {
    throw new IllegalTransitionError(machineName, from, to, via);
  }
}
